import { EnvironmentConfig, ENVIRONMENT_CONFIG } from '@web-client/shared';
import { Injectable, inject, signal, Injector } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, RouterStateSnapshot, TitleStrategy } from '@angular/router';

/**
 * Custom Title Strategy that supports dynamic title parts (e.g. blog title)
 * and appends a global suffix from the environment configuration.
 */
@Injectable({ providedIn: 'root' })
export class CustomPageTitleStrategy extends TitleStrategy {
    private readonly title = inject(Title);
    private readonly config = inject<EnvironmentConfig>(ENVIRONMENT_CONFIG);
    private readonly injector = inject(Injector);

    // Signal to store dynamic title parts (e.g. blog title)
    private dynamicTitle = signal<string | null>(null);

    /**
     * Sets a dynamic title part that overrides the route config title.
     * Useful for asynchronously loaded data like blog posts.
     */
    setDynamicTitle(newTitle: string | null) {
        this.dynamicTitle.set(newTitle);
        // Get Router lazily to avoid circular dependency
        const router = this.injector.get(Router);
        this.updateTitle(router.routerState.snapshot);
    }

    override updateTitle(snapshot: RouterStateSnapshot): void {
        const routeTitle = this.buildTitle(snapshot);
        const dynamic = this.dynamicTitle();
        const suffix = this.config.appTitleSuffix;

        // Fallback to data['title'] if route title is not set
        let dataTitle: string | undefined;
        let route = snapshot.root;
        while (route.firstChild) {
            route = route.firstChild;
            if (route.data && route.data['title']) {
                dataTitle = route.data['title'];
            }
        }

        const mainTitle = dynamic || routeTitle || dataTitle;

        if (mainTitle) {
            this.title.setTitle(`${mainTitle} - ${suffix}`);
        } else {
            this.title.setTitle(suffix);
        }
    }
}
